/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";

import { useState } from "react";

import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

import { useTableStoreByRoute } from "@/stores/useTableStore";

import { FileDraftOutlineIcon } from "@dotnet/telematics-design-system";

import {
  getDetailsDataValues,
  ICON_DEFAULT_STYLE,
  TABLE_NAMES
} from "../../../../../../shared";
import Button from "../../../../../Button/Button";
import ListChoice from "../../../../../ListChoice/ListChoice";
import Tooltip from "../../../../../Tooltip/Tooltip";
import { DetailsDataType } from "../../../../types";

interface OpenFilesButtonProps {
  detailsData: DetailsDataType | undefined;
  resourceName: TABLE_NAMES | undefined;
  menuChoice?: boolean;
}

const OpenFilesButton = ({
  detailsData,
  resourceName,
  menuChoice
}: OpenFilesButtonProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { hasFiles, number: vehicleNumber, id, cardName } = detailsData ?? {};

  const detailsDataFromTableName = () => {
    if (resourceName === TABLE_NAMES.Vehicles) {
      return getDetailsDataValues(TABLE_NAMES.Vehicles, id, vehicleNumber);
    }

    return getDetailsDataValues(TABLE_NAMES.Drivers, id, cardName);
  };

  const { filterName, filterValue, pageLocation } = detailsDataFromTableName();
  const { setParams } = useTableStoreByRoute(pageLocation);

  const [isDisabled, setIsDisabled] = useState(!hasFiles);

  const handleOpen = () => {
    const newFilterParams = {
      [filterName]: [filterValue]
    };

    setParams({
      filters: newFilterParams
    });

    setIsDisabled(true);

    navigate(pageLocation);
  };

  const buttonIcon = (
    <FileDraftOutlineIcon
      css={css({ marginRight: "-2px", ...ICON_DEFAULT_STYLE })}
    />
  );

  return resourceName === TABLE_NAMES.Drivers ||
    resourceName === TABLE_NAMES.Vehicles ? (
    <Tooltip
      placement="top-end"
      size="medium"
      disableHoverListener={hasFiles}
      title={t("Details##open files disabled tooltip")}
    >
      <div>
        {menuChoice ? (
          <ListChoice
            title={t("Details##open files")}
            iconLeft={buttonIcon}
            onClick={handleOpen}
            alignIconCenter
            disabled={isDisabled}
          />
        ) : (
          <Button
            data-testid="open-files-button"
            color="secondary"
            onClick={handleOpen}
            size="tiny"
            variant="iconLeft"
            disabled={isDisabled}
            icon={buttonIcon}
          >
            {t("Details##open files")}
          </Button>
        )}
      </div>
    </Tooltip>
  ) : (
    <></>
  );
};

export default OpenFilesButton;
